<template>
  <div>
    <ShippingEligibilityInfo ref="eligibility" />
    <div v-loading="loading">
      <el-row
          v-if="!isOneOff"
          :gutter="20"
          class="form-row">
        <el-col :span="24">
          <label>Old Product</label>
          <div class="el-input">
            <el-input
                type="text"
                disabled
                :value="oldProductDescription" />
          </div>
        </el-col>
      </el-row>
      <el-row
          v-if="productSkus.length > 0 && !isOneOff"
          :gutter="20"
          class="form-row">
        <el-col
            :span="24">
          <label>Replacement Product</label>
          <div class="el-input">
            <el-select
                v-model="request_product.device_price.id"
                filterable
                reserve-keyword
                clearable
                autocomplete="off"
                :disabled="!enabledExchange || loadingPrices"
                placeholder="Please enter a keyword"
                name="product_type"
                @change="setDevicePrice">
              <el-option
                  v-for="price in productSkus"
                  :key="price.id"
                  :disabled="isDisabledReplacementProduct(price)"
                  :label="humanFriendlyPrice(price)"
                  :value="price.id">
                <span :class="{ 'orange': price.out_of_stock }">{{ humanFriendlyPrice(price) }} </span>
              </el-option>
            </el-select>
          </div>
        </el-col>
      </el-row>
      <el-row
          v-if="productSkus.length === 0"
          :gutter="20"
          class="form-row">
        <el-col
            :span="12">
          <label>Product Type</label>
          <RefreshProducts v-if="!loadingPrices" />
          <div class="el-input">
            <el-select
                v-model="request_product.new_device_kind"
                filterable
                reserve-keyword
                clearable
                autocomplete="off"
                :disabled="!enabledExchange || loadingPrices"
                placeholder="Please enter a keyword"
                name="product_type"
                @change="productChanged">
              <el-option
                  v-for="device in availableProducts"
                  :key="device.id"
                  :disabled="productRestricted(device)"
                  :label="device.description"
                  :value="device.kind">
                <span style="float: left">{{ device.description }}</span>
                <span style="float: right; color: #8492a6; font-size: 13px">{{ device.enabled ? "" : "Disabled" }}</span>
              </el-option>
            </el-select>
          </div>
        </el-col>
        <el-col
            :span="12">
          <label>Color</label>
          <div class="el-input">
            <el-select
                v-model="request_product.new_device_color"
                filterable
                clearable
                autocomplete="off"
                :disabled="currentColors.length < 1"
                placeholder="Please select color"
                name="product_color">
              <el-option
                  v-for="color in currentColors"
                  :key="color.name"
                  :label="color.name"
                  :value="color.name" />
            </el-select>
          </div>
        </el-col>
      </el-row>
      <div>
        <small
            v-if="!enabledExchange"
            style="color: #F12C2C; margin: 0px 5px;">
          Exchange is not allowed for this device
        </small>
        <label
            v-if="isBaseStationV1"
            style="color: red">For base station replacements, please reference Alarm Desktop for region and version type
        </label>
      </div>
      <el-row
          :gutter="20"
          class="form-row">
        <el-col :span="24">
          <label>Reason Group</label>
          <el-tooltip
              class="item"
              effect="dark"
              content="Refresh data"
              placement="top">
            <RefreshButton
                :text="false"
                @click="refreshReasons" />
          </el-tooltip>
          <div class="el-input">
            <el-select
                v-model="request_reasons.reason_group"
                filterable
                clearable
                autocomplete="off"
                :disabled="!request_product.new_device_kind"
                placeholder="Please select group"
                name="reason_group"
                @change="resetReason()">
              <el-option
                  v-for="group in currentGroups"
                  :key="group.id"
                  :label="group.value"
                  :value="group.id" />
            </el-select>
          </div>
        </el-col>
      </el-row>
      <el-row
          :gutter="20"
          class="form-row">
        <el-col :span="24">
          <div
              class="clearfix"
              style="margin-bottom: 5px;">
            <label>
              Reason
              <el-tag
                  v-if="request_reasons.return_label"
                  type="warning"
                  effect="plain"
                  size="mini">With Return Label</el-tag>
            </label>
          </div>
          <div class="el-input">
            <el-select
                v-model="new_reason.id"
                filterable
                clearable
                autocomplete="off"
                :disabled="!request_reasons.reason_group"
                placeholder="Please select reason"
                name="reason"
                @change="setReturnLabel">
              <el-option
                  v-for="reason in currentReasons"
                  :key="reason.id"
                  :label="`${reason.value} - ${reason.text}`"
                  :value="reason.id">
                <span style="float: left">{{ reason.value }} - {{ reason.text }}</span>
                <span style="float: right; color: #8492a6; font-size: 13px">{{ reason.return_label ? 'With Return Label' : '' }}</span>
              </el-option>
            </el-select>
          </div>
        </el-col>
      </el-row>

      <el-alert
          v-if="isAllProductsOutOfStock"
          title="This product is out of stock."
          type="error"
          show-icon>
        <span>All of the items are out of stock, please consult your supervisor on how to proceed
        </span>
      </el-alert>

      <div class="center-buttons">
        <el-button
            plain
            @click="back()">
          Back
        </el-button>
        <el-button
            :disabled="isAllProductsOutOfStock"
            type="info"
            plain
            @click="reset()">
          Reset
        </el-button>
        <el-button
            :disabled="!isValid"
            type="primary"
            plain
            @click="next()">
          Next
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers as h } from "vuex";
import { replacementAllowedRegexp } from "./replacementHelper";
import { get, isEmpty, clone, cloneDeep, orderBy } from "lodash";
import ShippingEligibilityInfo from "@/containers/CustomerRequest/CustomerRequestComponents/ShippingEligibilityInfo";
import RefreshProducts from "@/components/Shared/Products/RefreshProducts";
import TimeZoneFormatter from "@/helpers/TimeZoneFormatterHelper";
import permission from "@/mixins/permission";
import RefreshButton from "@/containers/Customer/RefreshButton.vue";

const { mapGetters: sharedGetters, mapActions: sharedActions } = h("shared");
const { mapActions: requestActions, mapGetters: requestGetters } =
  h("db/requests");

export default {
  name: "CustomerRequestProductStep",
  components: {
    RefreshButton,
    ShippingEligibilityInfo,
    RefreshProducts,
  },
  mixins: [permission],
  props: {
    device: {
      type: Object,
      required: false,
      default: () => {
        return {};
      },
    },
    oowBaypassed: {
      type: Boolean,
      required: false,
      default: () => {
        return false;
      },
    },
  },
  data() {
    return {
      productsBlacklisted: [],
      shippingEligibility: {
        restrictedKinds: [],
        validating: false,
        satisfied: true,
        errors: [],
      },
      productSkus: [],
      loadingPrices: false,
      valid: false,
      currentGroup: null,
      currentGroups: [],
      currentReasons: [],
      selectedDevice: { options: {} },
      loading: false,
      new_reason: { quantity: 1, id: null },
      request_product: {
        new_device_kind: null,
        new_device_color: null,
        device_price: null,
      },
      request_reasons: {
        reason_group: null,
        reasons: [],
      },
      isAllProductsOutOfStock: false,
    };
  },
  computed: {
    ...sharedGetters(["products", "reasons", "groups"]),
    ...requestGetters(["requestInProgress"]),
    isBaseStationV1() {
      const type = this.device.kind || this.device.type;
      return type === "base_station_v1";
    },
    oldProductDescription() {
      var desc = this.currentProduct.description;
      const color = get(this.device, "profile.color") || "undefined";
      if (color !== "undefined") desc += `- ${color}`;

      return desc;
    },
    enabledProducts() {
      return this.products.filter((p) => p.enabled);
    },
    currentColors() {
      if (this.isOneOff) {
        // eslint-disable-next-line
        this.selectedDevice = this.products.find(
          (p) => p.kind === this.request_product.new_device_kind
        );
      }

      return !this.selectedDevice
        ? []
        : this.selectedDevice.options.colors || [];
    },
    availableProducts() {
      if (this.isOneOff) {
        return this.enabledProducts;
      }

      if (this.device.isAccessory) {
        return this.enabledProducts.filter(
          (item) => item.kind === this.device.kind
        );
      }

      let regexp = replacementAllowedRegexp(this.device.type);
      if (!regexp) return [];
      return this.enabledProducts.filter((item) =>
        Boolean(item.kind.match(regexp))
      );
    },
    enabledExchange() {
      if (this.requestType) {
        if (this.isOneOff) return true;
        else if (this.device.isAccessory) {
          return true;
        } else {
          const stock = get(this.device, "profile.current_stock", null);
          return (
            !stock ||
            stock.toLowerCase().trim() === "fresh lot" ||
            (stock.toLowerCase().trim() === "warehouse deal" &&
              this.oowBaypassed)
          );
        }
      } else {
        return false;
      }
    },
    isValid() {
      return (
        (this.currentColors.length < 1 ||
          !!this.request_product.new_device_color) &&
        !!this.request_product.new_device_kind &&
        !!this.request_reasons.reason_group &&
        !!this.new_reason.id
      );
    },
    requestType() {
      return this.requestInProgress.general.type;
    },
    isOneOff() {
      return this.requestType === "one_off";
    },
    isReplacement() {
      return this.requestType === "replacement";
    },
    currentKind() {
      return this.device.type || this.device.kind || this.device.profile.kind;
    },
    currentProduct() {
      return this.products.find((p) => p.kind === this.currentKind);
    },
  },
  watch: {
    reasons() {
      this.handleCurrentGroups();
    },
  },
  created() {
    this.parseDataFromStore();
  },
  mounted() {
    if (!this.isOneOff) this.fetchPrices();

    this.refreshReasons();
    let kind =
      get(this.request_product, "device_price.product.kind") ||
      get(this.request_product, "new_device_kind");
    this.productShippingEligibility(kind);
  },
  methods: {
    get,
    ...requestActions([
      "setProductInformation",
      "setReasonInformation",
      "fetchProductSkus",
    ]),
    ...sharedActions(["fetchReasons", "fetchProducts"]),
    handleCurrentGroups() {
      if (this.requestType) {
        // filter groups by request type
        let groups = this.groups.filter(
          (group) => group.key == this.requestType.toLowerCase()
        );

        // filter groups by device kind
        const deviceKind = this.isReplacement
          ? this.currentKind
          : this.request_product.new_device_kind;
        if (deviceKind) {
          groups = groups.filter(
            (group) =>
              !!this.reasons.find(
                (reason) =>
                  reason.reason_group_id == group.id &&
                  reason.kinds.includes(deviceKind)
              )
          );
        }

        this.currentGroups = groups;
      } else {
        this.currentGroups = [];
      }

      this.request_reasons.reason_group = null;
      this.request_reasons.return_label = false;
      this.request_reasons.reasons = [{}];
      this.new_reason = { id: "", quantity: 1 };
    },
    humanFriendlyPrice(price) {
      var text = price.product.description;

      if (price.color) text += ` - ${price.color}`;

      if (price.stock) text += ` - stock ${price.stock.toUpperCase()}`;

      if (price.out_of_stock) {
        if (price.restock_dt === null) {
          text += ` - Out of stock.`;
        } else {
          text += ` - Out of stock. Estimated available on ${this.formatRestockDate(
            price.restock_dt
          )}.`;
        }
      }

      return text;
    },
    formatRestockDate(date) {
      date = TimeZoneFormatter.formattedTime(date);
      return date.slice(0, -9);
    },
    filterGroupReasons() {
      this.currentReasons = [];
      let deviceKind = this.isReplacement
        ? this.currentKind
        : this.request_product.new_device_kind;

      this.currentReasons = this.reasons.filter(
        (reason) =>
          this.isReasonPartOfGroup(reason) && reason.kinds.includes(deviceKind)
      );
    },
    initDevicePrice() {
      this.request_product.device_price = cloneDeep(
        this.findBestMatchPrice() || this.productSkus[0]
      );

      this.request_product.new_device_kind =
        this.request_product.device_price.product.kind;
      this.request_product.new_device_color =
        this.request_product.device_price.color;

      this.request_product.sku = this.request_product.device_price.sku;

      this.showUnavailableProducts();
    },
    fetchPrices() {
      this.loadingPrices = true;
      this.fetchProductSkus({
        deviceId: this.requestInProgress.general.profile.deviceId,
        country: this.requestInProgress.address.customer_country,
        state: this.requestInProgress.address.customer_state,
      })
        .then((response) => {
          this.productSkus = orderBy(response.data.skus, ["priority"], ["asc"]);
          this.initDevicePrice();
          this.validateReplacementAvailability(this.productSkus);
        })
        .finally(() => (this.loadingPrices = false));
    },
    showUnavailableProducts() {
      const unavailableProducts = this.productSkus
        .filter((price) => !price.shipping_eligible)
        .map((price) => this.humanFriendlyPrice(price));
      if (unavailableProducts.length > 0)
        this.$refs.eligibility.display(unavailableProducts);
    },
    findBestMatchPrice() {
      const bestMatch = this.productSkus.find(
        (sku) => sku["best_match"] == true
      );
      return bestMatch;
    },
    isReasonPartOfGroup(reason) {
      return this.request_reasons.reason_group == reason.reason_group_id;
    },
    productRestricted(device) {
      return this.productsBlacklisted.includes(device.kind) || !device.enabled;
    },
    parseDataFromStore() {
      if (Object.keys(this.requestInProgress.product).length > 0) {
        this.request_product = cloneDeep(this.requestInProgress.product);
      }

      if (!this.enabledExchange) this.setDefaultData();

      if (Object.keys(this.requestInProgress.reasons).length > 0) {
        this.request_reasons.reasons = clone(
          this.requestInProgress.reasons.reasons
        );

        this.request_reasons.reason_group = clone(
          this.requestInProgress.reasons.reason_group
        );

        this.request_reasons.return_label = clone(
          this.requestInProgress.reasons.return_label
        );

        this.new_reason = clone(this.request_reasons.reasons[0] || {});
      }
    },
    addNewReason() {
      this.request_reasons.reasons[0] = this.new_reason || {};
    },
    setDefaultData() {
      this.setNewDevice(this.device.type);
    },
    next() {
      this.request_reasons.reasons = [this.new_reason];
      this.setProductInformation(this.request_product);
      this.setReasonInformation(this.request_reasons);
      this.$emit("next");
    },
    reset() {
      this.setProductInformation({}).then(() => {
        if (this.productSkus.length && !this.isOneOff) {
          this.initDevicePrice();
        } else {
          this.request_product = {
            new_device_color: null,
          };
          if (!this.enabledExchange) {
            this.setDefaultData();
          } else {
            this.request_product.new_device_kind = null;
          }
        }
      });

      this.setReasonInformation({}).then(() => {
        this.request_reasons = {
          reason_group: null,
          reasons: [],
        };
        this.new_reason = { quantity: 1, id: null };
      });
    },
    back() {
      this.$emit("back");
    },
    setDevicePrice(skuId) {
      const selectedSku = this.productSkus.find((sku) => sku.id === skuId);

      this.request_product.device_price = cloneDeep(selectedSku);

      this.request_product.new_device_color =
        this.request_product.device_price.color;

      this.request_product.new_device_kind =
        this.request_product.device_price.product.kind;

      this.request_product.sku = selectedSku.sku;

      this.handleCurrentGroups();
    },
    productChanged(product) {
      this.productShippingEligibility(product, true);
    },
    productShippingEligibility(productKind, clearData) {
      if (isEmpty(productKind)) return;
      let { customer_country: country_code, customer_state: state_code } =
        this.requestInProgress.address;
      this.$refs.eligibility
        .evaluate({
          product_kinds: [productKind],
          country_code,
          state_code,
        })
        .then((satisfied) => {
          if (!satisfied) {
            this.selectedDevice = { options: {} };
            this.request_product.new_device_kind = null;
            this.request_product.new_device_color = null;
            this.request_reasons.reason_group = null;
            this.new_reason = { id: "", quantity: 1 };
            this.request_product.sku = null;
            this.request_product.device_price = {};
            this.productsBlacklisted.push(productKind);
          } else {
            this.setNewDevice(productKind, clearData);
          }
        });
    },
    setReturnLabel(selectedReason) {
      let selectedId = selectedReason;
      let reason = this.currentReasons.find((r) => r.id === selectedId);
      this.request_reasons.return_label = reason.return_label;
    },
    setNewDevice(newDevice, resetValues) {
      this.selectedDevice = this.products.find((p) => p.kind === newDevice);
      if (resetValues) {
        this.request_product.new_device_kind = newDevice;
        this.request_product.new_device_color = "";
        this.request_reasons.reason_group = "";
        this.new_reason = { id: "", quantity: 1 };
      }

      this.handleCurrentGroups();
    },
    refreshReasons() {
      this.fetchReasons().finally(
        () => (this.request_reasons.reason_group = null)
      );
    },
    getReason(id) {
      return (
        this.reasons.find((r) => {
          return r.id === id;
        }) || {}
      );
    },
    resetReason() {
      this.request_reasons.return_label = false;
      this.request_reasons.reasons = [{}];
      this.new_reason = { id: "", quantity: 1 };
      this.filterGroupReasons();
    },
    isDisabledReplacementProduct(product) {
      const isOutOfStockWithoutPermissions =
        product.out_of_stock &&
        !this.hasPermission("CustomerRequest", "allow-out-of-stock");

      if (!product.shipping_eligible || isOutOfStockWithoutPermissions) {
        return true;
      }

      return false;
    },
    validateReplacementAvailability(productSkus) {
      if (this.hasPermission("CustomerRequest", "allow-out-of-stock")) {
        return;
      }

      const similarProducts = productSkus || [];

      let outOfStockProducts = similarProducts.filter(
        (product) => product.out_of_stock
      );

      if (similarProducts.length === outOfStockProducts.length) {
        this.request_product.device_price.id = null;
        this.request_product.new_device_color = null;
        this.request_product.new_device_kind = null;
        this.request_product.sku = null;

        this.isAllProductsOutOfStock = true;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.el-select {
  width: 100%;
}
.orange {
  color: #a99877;
}
</style>
